import {
  AuthInfo,
  encodeToken,
  getCookieTokens,
  getEnvironment,
  getUserInfoFromTokens,
  Tokens,
} from '@schemeserve/global-utils';
import { intervalToDuration, isAfter } from 'date-fns';
import { pipe } from 'fp-ts/lib/function';
import * as B from 'fp-ts/lib/boolean';
import * as O from 'fp-ts/lib/Option';
import * as TE from 'fp-ts/lib/TaskEither';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { convertToUTC, EXPIRY_PERIOD, logger, withRetries } from './Auth';

const AuthErrors = {
  MissingTokens: 'token not present',
  UnableToEncodeTokens: 'Unable to encode tokens',
  RequestFailed: 'token request failed',
  RequestJsonMalformed: 'token request response malformed',
} as const;
type AuthError = (typeof AuthErrors)[keyof typeof AuthErrors];

type RefreshRequestOptions =
  | {
      method: 'get';
    }
  | {
      method: 'post';
      body: string;
      headers: Record<string, string>;
    };

function buildRequestOptions(tokens: Tokens): RefreshRequestOptions {
  const method = O.isSome(tokens.refreshToken) ? 'post' : 'get';
  const refreshToken = pipe(
    tokens.refreshToken,
    O.map((refreshToken) => refreshToken),
    O.getOrElse(() => ''),
  );
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `bearer ${tokens.accessToken}`,
  };

  return method === 'get'
    ? { method }
    : {
        method,
        headers,
        body: JSON.stringify({ refreshToken }),
      };
}

function overExpiryPeriod(
  expiryPeriod: number,
  expiry: string,
  currentDate: string,
): boolean {
  return pipe(
    intervalToDuration({
      start: currentDate,
      end: expiry,
    }),
    (remaining) => remaining?.minutes ?? 0,
    (minutes) => minutes <= expiryPeriod,
  );
}

function shouldMakeRefreshRequest(tokens: Tokens): boolean {
  const expiry = convertToUTC(new Date(tokens.expiry).toISOString());
  const currentDate = convertToUTC(new Date().toISOString());

  return pipe(
    isAfter(currentDate, expiry),
    B.match(
      () => overExpiryPeriod(EXPIRY_PERIOD, expiry, currentDate),
      () => true,
    ),
  );
}

function fetchNewTokens(
  requestOptions: RefreshRequestOptions,
  url: string,
): TE.TaskEither<{ status: number; error: AuthError; }, AuthInfo> {
  return pipe(
    TE.tryCatch(
      () => fetch(url, requestOptions),
      (reason) => {
        console.error(reason);
        return {
          status: 500,
          error: AuthErrors.RequestFailed as AuthError,
        }
      },
    ),
    TE.chain((res) => {
      if (!res.ok) return TE.left({ status: res.status, error: AuthErrors.MissingTokens as AuthError });
      return TE.right(res);
    }),
    TE.chain((res) =>
      TE.tryCatch(
        () => res.json() as Promise<AuthInfo>,
        (reason) => {
          console.error(`Unable to parse JSON: ${String(reason)}`);
          return {
            status: res.status,
            error: AuthErrors.RequestFailed as AuthError,
          }
        },
      ),
    ),
  );
}

// NOTE double check if we need this funciton
export function userIsLoggedIn(metricLogger: ApplicationInsights): boolean {
  return pipe(
    getCookieTokens(),
    getUserInfoFromTokens,
    O.tap((userDetails) => {
      metricLogger.setAuthenticatedUserContext(
        String(userDetails.UserId),
        String(userDetails.AccountId),
        true,
      );

      const payload = {
        name: 'UserLoggedIn',
        properties: {
          accessGroup: userDetails.AccessGroup,
          MasterAccountId: userDetails.MasterAccountId,
          MasterUrl: userDetails.MasterUrl,
          AccountId: userDetails.AccountId,
          email: userDetails.PrimaryEmail,
          userId: userDetails.UserId,
          username: userDetails.Username,
          env: getEnvironment(window.location.href),
        },
      };
      metricLogger.trackEvent(payload);

      return O.some(userDetails);
    }),
    O.match(
      () => false,
      () => true,
    ),
  );
}

export function newAuthProcess(
  url: string,
  metricLogger: ApplicationInsights,
): TE.TaskEither<AuthError, Tokens> {
  return pipe(
    TE.Do,
    TE.tap(() => TE.rightIO(() => console.debug('running auth process'))),
    TE.bind('tokens', () =>
      pipe(
        getCookieTokens(),
        TE.fromOption(() => AuthErrors.MissingTokens as AuthError),
      ),
    ),
    TE.bind('shouldMakeRequest', ({ tokens }) =>
      TE.right(shouldMakeRefreshRequest(tokens)),
    ),
    TE.bind('requestOptions', ({ tokens }) =>
      TE.right(buildRequestOptions(tokens)),
    ),
    TE.chain(({ tokens, shouldMakeRequest, requestOptions }) =>
      pipe(
        shouldMakeRequest,
        B.match(
          () => TE.right(tokens),
          () => pipe(
            withRetries<{ status: number; error: AuthError }, AuthInfo>(fetchNewTokens(requestOptions, url), 3),
            TE.mapLeft((error) => error.error),
            TE.chain((tokens) =>
              pipe(
                tokens,
                encodeToken,
                TE.fromOption(() => AuthErrors.UnableToEncodeTokens as AuthError),
              ),
            ),
          ),
        ),
      ),
    ),
  );
}

export async function newLogout(
  metricLogger: ApplicationInsights,
  reason?: string,
): Promise<void> {
  logger('LoggingUserOut');
  console.debug('LoggingUserOut');
  metricLogger.trackEvent({
    name: 'LoggingUserOut',
    properties: {
      reason,
      env: getEnvironment(window.location.href),
    },
  });
  metricLogger.clearAuthenticatedUserContext();
}
